import type { BoxProps } from '@chakra-ui/react';
import { useState, useEffect } from 'react';

/**
 * Hook that smoothly transitions between loading skeleton and content elements.
 * Requires an outer container, a skeleton, and one or more content elements.
 * The outer container will be made position relative, the skeleton will position absolutely and fill the outer container, and the content elements will remain in normal flow.
 *
 * @param isLoading - Whether content is currently loading
 * @param options - Configuration options for fade durations
 * @returns Object containing Chakra props for container, skeleton, and content
 */
export const useSkeletonOverlay = (
  isLoading: boolean,
  options: {
    /** Duration of the fade-out animation in milliseconds */
    fadeOutDuration?: number;
    /** Duration of the fade-in animation in milliseconds */
    fadeInDuration?: number;
  } = {},
): {
  /** Props to spread onto the container (position: relative) */
  containerProps: Pick<BoxProps, 'position'>;
  /** Props to spread onto the skeleton element (absolute positioning, opacity, transition) */
  skeletonProps: Pick<
    BoxProps,
    | 'position'
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'opacity'
    | 'transition'
    | 'zIndex'
    | 'pointerEvents'
  >;
  /** Props to spread onto the content element (opacity, transition) */
  contentProps: Pick<BoxProps, 'opacity' | 'transition'>;
} => {
  const { fadeOutDuration = 300, fadeInDuration = 200 } = options;

  // Track skeleton visibility for fade-out animation
  const [showSkeleton, setShowSkeleton] = useState(true);

  // Handle fade-out when content finishes loading
  useEffect(() => {
    if (!isLoading && showSkeleton) {
      // Start fade-out animation immediately
      setShowSkeleton(false);
    } else if (isLoading) {
      // Reset skeleton visibility when loading starts
      setShowSkeleton(true);
    }
  }, [isLoading, showSkeleton]);

  const containerProps: Pick<BoxProps, 'position'> = {
    position: 'relative',
  };

  const skeletonProps: Pick<
    BoxProps,
    | 'position'
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'opacity'
    | 'transition'
    | 'zIndex'
    | 'pointerEvents'
  > = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: showSkeleton ? 1 : 0,
    transition: `opacity ${fadeOutDuration}ms ease-in-out`,
    zIndex: showSkeleton ? 1 : -1,
    pointerEvents: showSkeleton ? 'auto' : 'none',
  };

  const contentProps: Pick<BoxProps, 'opacity' | 'transition'> = {
    opacity: showSkeleton ? 0 : 1,
    transition: `opacity ${fadeInDuration}ms ease-in-out`,
  };

  return {
    containerProps,
    skeletonProps,
    contentProps,
  };
};
